import React from 'react';
import PropTypes from 'prop-types';
import { FaHandHoldingHeart, FaUserNurse, FaHome, FaShieldAlt, FaClock, FaWhatsapp, FaPhone } from 'react-icons/fa';
import Seo from './common/Seo';
import { COMPANY_INFO, } from '../constants';
import somosImage from '../assets/somos.png';
import visionimg from '../assets/vision.png';

const AboutSection = ({
  title,
  content,
  image,
  alt,
  reverse = false
}) => (
  <section className={`relative py-16 md:py-20 lg:py-24 ${reverse ? 'bg-gray-50' : ''}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className={`grid md:grid-cols-2 gap-12 pt-10 lg:gap-16 items-center ${reverse ? 'md:grid-flow-dense' : ''}`}>
        {/* Contenedor de imagen */}
        <div className={`relative ${reverse ? 'md:col-start-2' : ''} flex items-stretch`}>
          <div className="relative overflow-hidden rounded-2xl shadow-2xl transform hover:shadow-3xl transition-shadow duration-300 w-full">
            <img
              src={image}
              alt={alt}
              className="w-full h-full min-h-[500px] md:min-h-[600px] object-cover transform transition-transform duration-500 hover:scale-105"
              loading="lazy"
              width="600"
              height="600"
            />
            <div className="absolute inset-0 bg-gradient-to-r" />
          </div>
        </div>

        {/* Contenedor de contenido */}
        <div className={`${reverse ? 'md:col-start-1 md:text-right' : ''}`}>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className={`h-1 w-12 bg-blue-600 ${reverse ? '' : ''}`}></div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                {title}
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
              {content}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

AboutSection.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  image: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  reverse: PropTypes.bool
};

function AboutUs() {
  // Funciones para manejar los botones
  const handlePhoneCall = () => {
    window.open(`tel:${COMPANY_INFO.phone}`, '_self');
  };

  const handleWhatsAppInfo = () => {
    const message = `Hola ${COMPANY_INFO.name},

Me interesa conocer con mayor detalle sus servicios de enfermería y cuidadoras especializadas a domicilio en Guadalajara.

En particular, quisiera información sobre:
- Servicios de enfermería geriátrica disponibles
- Perfil y certificaciones del personal
- Planes de atención personalizados
- Cobertura en mi zona

Quedo en espera de su atención.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${COMPANY_INFO.whatsapp}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppQuote = () => {
    const message = `Hola ${COMPANY_INFO.name},

Me gustaría solicitar información para diseñar un plan de cuidado personalizado para un familiar.

Datos de referencia:
- Tipo de servicio requerido: [Enfermero/Cuidadora/Enfermería general]
- Modalidad de atención: [Especificar]
- Zona en Guadalajara: [Especificar]
- Condición y necesidades del paciente: [Descripción breve]
- Fecha estimada de inicio: [Fecha]

Agradezco puedan brindarme orientación al respecto.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${COMPANY_INFO.whatsapp}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "Geriatría Serena",
    "description": "Agencia de excelencia en enfermería geriátrica y cuidados domiciliarios de élite en Guadalajara. Personal certificado con riguroso control de confianza, planes VIP personalizados y supervisión médica continua.",
    "url": "https://geriatriaserena.com",
    "telephone": "+52-33-2545-6522",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Av. Principal 123",
      "addressLocality": "Guadalajara",
      "addressRegion": "Jalisco",
      "postalCode": "44100",
      "addressCountry": "MX"
    },
    "medicalSpecialty": [
      "Geriatría",
      "Enfermería Domiciliaria",
      "Cuidados Paliativos"
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 20.6597,
        "longitude": -103.3496
      },
      "geoRadius": "50000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Enfermería y Cuidado a Domicilio",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Enfermeros a Domicilio",
            "description": "Servicio profesional de enfermería especializada a domicilio las 24 horas"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cuidadoras a Domicilio",
            "description": "Cuidadoras certificadas para adultos mayores con atención personalizada"
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "foundingDate": "2025"
  };

  return (
    <>
      <Seo
        title="Geriatría Serena — Enfermería Geriátrica de Élite a Domicilio en Guadalajara"
        description="Servicio exclusivo de enfermería geriátrica y cuidadoras especializadas a domicilio en Guadalajara. Personal certificado con riguroso control de confianza, planes de cuidado VIP personalizados y supervisión médica continua. La paz mental que su familia merece."
        keywords="enfermería geriátrica élite guadalajara, cuidadoras especializadas certificadas VIP, enfermería domiciliaria premium, cuidado adultos mayores de lujo, geriatría guadalajara jalisco, atención médica domiciliaria exclusiva, estándares hospitalarios a domicilio"
        canonical="https://geriatriaserena.com/about-us"
        robots="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        jsonLd={structuredData}
        meta={[
          { property: 'og:title', content: 'Geriatría Serena — Enfermería Geriátrica de Élite a Domicilio en Guadalajara' },
          { property: 'og:description', content: 'Servicio exclusivo de enfermería y cuidado domiciliario para adultos mayores en Guadalajara. Personal certificado con control de confianza, planes VIP personalizados y supervisión médica continua.' },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: 'https://geriatriaserena.com/about-us' },
          { property: 'og:image', content: 'https://geriatriaserena.com/assets/somos.png' },
          { property: 'og:site_name', content: 'Geriatría Serena' },
          { property: 'og:locale', content: 'es_MX' },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'Geriatría Serena — Enfermería Geriátrica de Élite en Guadalajara' },
          { name: 'twitter:description', content: 'Personal médico certificado con riguroso control de confianza para cuidado domiciliario premium de adultos mayores en Guadalajara, Jalisco.' },
          { name: 'twitter:image', content: 'https://geriatriaserena.com/assets/somos.png' },
          { name: 'author', content: 'Geriatría Serena' },
          { name: 'geo.region', content: 'MX-JAL' },
          { name: 'geo.placename', content: 'Zapopan, Jalisco' },
          { name: 'geo.position', content: '20.71499;-103.37659' },
          { name: 'ICBM', content: '20.71499, -103.37659' }
        ]}
      />

      <main className="overflow-hidden">
        {/* Hero Section with Local SEO - Mejorado visualmente */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 py-20 md:py-28 mb-12 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black bg-opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
                <span className="block mb-3">Enfermería Geriátrica de Élite</span>
                <span className="block text-blue-200">a Domicilio en</span>
                <span className="block text-amber-300">Guadalajara, Jalisco</span>
              </h1>

              <div className="w-24 h-1 bg-amber-300 mx-auto mb-8"></div>
              
              <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-10">
                Servicio exclusivo de <strong className="text-white">enfermería geriátrica premium</strong> y{' '}
                <strong className="text-white">cuidadoras certificadas VIP</strong> para adultos mayores. 
                Planes de cuidado personalizados con estándares hospitalarios en la privacidad de su hogar.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                <div className="flex items-center gap-3 bg-white bg-opacity-20 backdrop-blur-sm px-6 py-4 rounded-full shadow-lg hover:bg-opacity-30 transition-all duration-300">
                  <FaUserNurse className="text-white text-xl" />
                  <span className="text-lg font-semibold">Personal Certificado</span>
                </div>
                <div className="flex items-center gap-3 bg-white bg-opacity-20 backdrop-blur-sm px-6 py-4 rounded-full shadow-lg hover:bg-opacity-30 transition-all duration-300">
                  <FaHome className="text-green-300 text-xl" />
                  <span className="text-lg font-semibold">Estándares Hospitalarios</span>
                </div>
                <div className="flex items-center gap-3 bg-white bg-opacity-20 backdrop-blur-sm px-6 py-4 rounded-full shadow-lg hover:bg-opacity-30 transition-all duration-300">
                  <FaClock className="text-white text-xl" />
                  <span className="text-lg font-semibold">Supervisión Continua 24/7</span>
                </div>
              </div>

              <div className="mt-12">
                <button
                  onClick={handleWhatsAppInfo}
                  className="bg-amber-400 hover:bg-amber-500 text-blue-900 font-bold py-4 px-8 rounded-full text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto"
                >
                  <FaWhatsapp className="text-2xl" />
                  Solicitar Información
                </button>
              </div>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent"></div>
        </section>

        <AboutSection
          title="Enfermería Geriátrica de Élite a Domicilio"
          content={
            <>
              <p className="mb-6">
                En <strong className="text-blue-600">Geriatría Serena</strong>, somos referente en
                <strong> servicios exclusivos de enfermería y cuidadoras geriátricas a domicilio</strong> en Guadalajara, Jalisco.
                Desde 2025, brindamos <strong>atención médica domiciliaria de primer nivel</strong>
                para adultos mayores con estándares hospitalarios premium y planes de cuidado personalizados VIP.
              </p>
              <button
                onClick={handleWhatsAppInfo}
                className="mb-8 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold inline-flex items-center gap-3 transition-all"
              >
                <FaWhatsapp className="text-xl" />
                Solicitar información
              </button>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Nuestro Servicio de Élite Incluye:
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <FaUserNurse className="w-6 h-6 text-blue-600 mt-1" />
                  <span><strong>Enfermeros titulados especializados en geriatría:</strong> Personal con cédula profesional vigente y experiencia certificada en cuidado de adultos mayores</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaHandHoldingHeart className="w-6 h-6 text-green-600 mt-1" />
                  <span><strong>Cuidadoras certificadas con control de confianza riguroso:</strong> Auxiliares de enfermería seleccionadas mediante verificación exhaustiva para cuidado integral VIP</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaShieldAlt className="w-6 h-6 text-blue-600 mt-1" />
                  <span><strong>Supervisión médica continua 24 horas:</strong> Presencia profesional permanente y respuesta inmediata ante cualquier eventualidad</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaHome className="w-6 h-6 text-green-600 mt-1" />
                  <span><strong>Cobertura en Guadalajara y Área Metropolitana:</strong> Servicio exclusivo en todas las zonas premium de la ciudad, incluyendo Providencia, Country Club y Puerta de Hierro</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-800 font-medium">
                  ✅ Todo nuestro <strong>personal de enfermería y cuidadoras</strong><br /> 
                  pasa por verificación de antecedentes, certificaciones vigentes y evaluación continua de desempeño
                </p>
              </div>
            </>
          }
          image={somosImage}
          alt="Enfermería geriátrica de élite — cuidadoras certificadas de Geriatría Serena brindando atención domiciliaria premium a adultos mayores en Guadalajara"
        />

        <AboutSection
          title="¿Por Qué Elegir Geriatría Serena para su Familiar?"
          content={
            <div className="space-y-8">
              <article className="relative pl-6 border-l-4 border-blue-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Misión: Excelencia Clínica con Calidez Humana
                </h3>
                <p className="text-gray-600">
                  Brindar <strong>cuidado geriátrico de élite</strong> mediante 
                  <strong> personal altamente certificado y con riguroso control de confianza</strong> que prioriza la dignidad, 
                  la autonomía y el bienestar integral del paciente en la privacidad de su hogar.
                </p>
              </article>

              <article className="relative pl-6 border-l-4 border-green-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Visión: Referente en Atención Médica Domiciliaria Premium
                </h3>
                <p className="text-gray-600">
                  Consolidarnos como el estándar de excelencia en <strong>servicios de enfermería y cuidado geriátrico a domicilio</strong> 
                  en el sector residencial de Guadalajara, ofreciendo siempre la misma calidad que las mejores instituciones hospitalarias privadas.
                </p>
              </article>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="font-semibold text-blue-900 mb-3">Ventajas Clínicas de Nuestros Enfermeros:</h4>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Evaluación clínica personalizada en su hogar</li>
                    <li>• Administración segura de medicamentos IV y orales</li>
                    <li>• Curaciones con protocolos hospitalarios premium</li>
                    <li>• Monitoreo y registro continuo de signos vitales</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-3">Beneficios de Nuestras Cuidadoras VIP:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Asistencia en actividades diarias</li>
                    <li>• Compañía y apoyo emocional</li>
                    <li>• Preparación de alimentos especiales</li>
                    <li>• Movilización y fisioterapia básica</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mt-8">
                <h4 className="font-semibold text-amber-900 mb-2">Garantías y Certificaciones de Élite</h4>
                <p className="text-amber-800">
                  Todo nuestro <strong>personal de enfermería y cuidadoras</strong> cuenta con:
                  cédula profesional vigente, verificación de antecedentes exhaustiva, 
                  seguro de responsabilidad civil y evaluaciones continuas de desempeño clínico.
                </p>
              </div>
            </div>
          }
          image={visionimg}
          alt="Enfermería geriátrica de élite — equipo certificado con estándares hospitalarios premium para cuidado domiciliario en Guadalajara"
          reverse={true}
        />

        {/* Local SEO Section - Actualizado para Guadalajara */}
        <section className="relative bg-gradient-to-br from-gray-50 to-gray-100 py-20 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute bottom-10 right-10 w-48 h-48 bg-gray-200 rounded-full opacity-15 animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-200 rounded-full opacity-10"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
                <FaHome className="text-white" />
                Cobertura Exclusiva en Guadalajara
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Enfermería Geriátrica de Élite<br />
                <span className="text-blue-600">en las Zonas Residenciales de Guadalajara</span>
              </h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Brindamos servicios exclusivos de <strong className="text-blue-700">enfermería geriátrica premium</strong> y{' '}
                <strong className="text-green-700">cuidadoras certificadas VIP</strong> en los sectores más selectos 
                de Guadalajara y la Zona Metropolitana.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FaUserNurse className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Zona Centro y Chapultepec</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Centro Histórico, Zona Chapultepec, Americana, Lafayette, Juárez, Santa Teresita
                </p>
                <div className="bg-blue-50 px-4 py-3 rounded-lg">
                  <span className="text-blue-700 font-semibold text-sm">
                    Personal certificado disponible 24/7
                  </span>
                </div>
              </div>
              
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-gradient-to-br from-green-500 to-green-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FaHandHoldingHeart className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Zona Providencia y Country</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Providencia, Del Valle, Country Club, Jardines del Bosque, Puerta de Hierro
                </p>
                <div className="bg-green-50 px-4 py-3 rounded-lg">
                  <span className="text-green-700 font-semibold text-sm">
                    Personal especializado en geriatría certificado
                  </span>
                </div>
              </div>
              
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="bg-gradient-to-br from-blue-700 to-blue-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FaShieldAlt className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Área Metropolitana</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Zapopan, Tlaquepaque, Tonalá, Tlajomulco
                </p>
                <div className="bg-blue-50 px-4 py-3 rounded-lg">
                  <span className="text-blue-800 font-semibold text-sm">
                    Atención médica domiciliaria con estándares hospitalarios
                  </span>
                </div>
              </div>
            </div>
            
            {/* Call to Action */}
            <div className="text-center mt-16">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-2xl shadow-2xl max-w-4xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  ¿Desea servicios de enfermería geriátrica de élite en Guadalajara?
                </h3>
                <p className="text-blue-100 text-lg mb-6">
                  Diseñamos un plan de atención exclusivo para su familiar en cualquier zona de la Zona Metropolitana de Guadalajara
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handlePhoneCall}
                    className="bg-amber-400 hover:bg-amber-500 text-blue-900 font-bold py-3 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaPhone className="text-lg" />
                    Llamar: (33) 2545-6522
                  </button>
                  <button 
                    onClick={handleWhatsAppQuote}
                    className="bg-white hover:bg-gray-100 text-blue-700 font-bold py-3 px-8 rounded-full text-lg shadow-lg border-2 border-white hover:border-gray-300 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaWhatsapp className="text-lg text-green-600" />
                    Solicitar Plan Personalizado
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AboutUs;
